// What a material picker offers (Spencer's change log, 2026-10-07): materials carrying a key
// ("exterior sheet", "edge banding": the material's Offered as list), or in one Admin005
// category and subcategory (Hardware · Pulls & Knobs), or both. One filter, two ways to say
// it; the same editor wherever a filter is set: a material standard, a role of a choice
// standard, a material input on a catalog item.

import type { LibMaterial, Library, MaterialFilter } from '@zerogap/engine';

const same = (a: string | null | undefined, b: string | null | undefined) => (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/** Whether a material passes a filter. No filter passes everything. */
export function materialFits(m: LibMaterial, filter: MaterialFilter | null | undefined): boolean {
  if (!filter) return true;
  if (filter.key && !(m.offered_as ?? []).some((k) => same(k, filter.key))) return false;
  if (filter.category && !same(m.category, filter.category)) return false;
  if (filter.subcategory && !same(m.subcategory, filter.subcategory)) return false;
  return true;
}

/** The filter in words: "offered as exterior sheet · Sheet Goods · Birch". Empty for no filter. */
export function filterWords(filter: MaterialFilter | null | undefined): string {
  if (!filter) return '';
  return [filter.key ? `offered as ${filter.key}` : '', filter.category ?? '', filter.subcategory ?? '', filter.family ? `in ${filter.family}` : ''].filter(Boolean).join(' · ');
}

/** Whether a catalog item passes a filter: only its family is read. No filter passes everything. */
export function catalogFits(c: { family?: string | null }, filter: MaterialFilter | null | undefined): boolean {
  if (!filter?.family) return true;
  return same(c.family, filter.family);
}

/** Every key a material is offered under, across the library. */
export function offeredKeys(lib: Library): string[] {
  const set = new Set<string>();
  for (const m of Object.values(lib.materials)) for (const k of m.offered_as ?? []) if (k.trim()) set.add(k.trim());
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** Key, category and subcategory, each a dropdown; a blank filter saves as none. */
export function FilterEditor({ lib, value, onChange, disabled }: { lib: Library; value: MaterialFilter | null | undefined; onChange: (f: MaterialFilter | null) => void; disabled?: boolean }) {
  const keys = offeredKeys(lib);
  const categories = [...new Set(Object.values(lib.materials).map((m) => m.category).filter((c): c is string => !!c))].sort((a, b) => a.localeCompare(b));
  const subcategories = [...new Set(Object.values(lib.materials).filter((m) => !value?.category || same(m.category, value.category)).map((m) => m.subcategory).filter((c): c is string => !!c))].sort((a, b) => a.localeCompare(b));
  const set = (patch: Partial<MaterialFilter>) => {
    const next: MaterialFilter = { ...(value ?? {}), ...patch };
    if (!next.key) delete next.key;
    if (!next.category) {
      delete next.category;
      delete next.subcategory;
    }
    if (!next.subcategory) delete next.subcategory;
    onChange(Object.keys(next).length ? next : null);
  };
  return (
    <span className="row mid" style={{ gap: 4, flexWrap: 'wrap' }}>
      <select disabled={disabled} value={value?.key ?? ''} onChange={(e) => set({ key: e.target.value || null })} title="Only materials offered under this key (set on the material)">
        <option value="">any key</option>
        {keys.map((k) => (
          <option key={k} value={k}>
            offered as {k}
          </option>
        ))}
        {value?.key && !keys.includes(value.key) && <option value={value.key}>offered as {value.key} (no material carries it yet)</option>}
      </select>
      <select disabled={disabled} value={value?.category ?? ''} onChange={(e) => set({ category: e.target.value || null, subcategory: null })} title="Only one Admin005 category">
        <option value="">any category</option>
        {categories.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      {value?.category && (
        <select disabled={disabled} value={value?.subcategory ?? ''} onChange={(e) => set({ subcategory: e.target.value || null })} title="Only one subcategory within the category">
          <option value="">any subcategory</option>
          {subcategories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      )}
    </span>
  );
}
